import { CriticalAlertsBoard } from '@/components/fraud-hub/CriticalAlertsBoard';
import { HubKpiTiles } from '@/components/fraud-hub/HubKpiTiles';
import { SeverityTrendChart } from '@/components/fraud-hub/SeverityTrendChart';
import { TopRulesPanel } from '@/components/fraud-hub/TopRulesPanel';
import { GeoClusterMap } from '@/components/fraud-hub/GeoClusterMap';
import { DrillDeeperLinks } from '@/components/fraud-hub/DrillDeeperLinks';
import { TopOffendersPanel } from '@/components/fraud-hub/TopOffendersPanel';
import { WeekendAfterHoursTiles } from '@/components/fraud-hub/WeekendAfterHoursTiles';
import { StationRiskTiles } from '@/components/fraud-hub/StationRiskTiles';
import { TimePeriodSelector } from '@/components/shared/TimePeriodSelector';

/**
 * /app/fraud-hub — Critical-first narrative landing page for fraud
 * investigators.
 *
 * Section flow (top → bottom mirrors at-a-glance → drill-down):
 *   0. Time period selector       — period-aware sections read this from
 *                                   `?period=`. Default `D` because
 *                                   investigators care about today; the
 *                                   Critical Board itself is period-
 *                                   independent (open is a status, not a
 *                                   window).
 *   1. Hub KPI tiles              — five at-a-glance numbers (Open
 *                                   Critical leads as the FIRST tile)
 *   2. Severity Trend chart       — daily volume + mix over the period
 *   3. Top Rules / Geo Clusters   — which rules + where (side-by-side lg)
 *   4. Critical Board / Top       — drill into the specific Critical
 *      Offenders                    alerts (left, period-independent)
 *                                   alongside the loudest vehicles
 *                                   (right, period-aware) on lg+.
 *   5. Drill-deeper links         — Superset dashboards for power-users
 *
 * Severity-first principle is preserved by page order (Open Critical
 * tile is the first content) plus the soft red palette differentiating
 * it from neutral tiles. The Critical Alerts grid (red border +
 * oversized severity badges) sits where investigators naturally drill
 * down after the at-a-glance summary.
 *
 * Architectural intent: at-a-glance summary above the fold, drill-down
 * to specifics below. Multi-severity triage stays in /app/fraud (Fraud
 * Center); deep-dive per-rule diagnostics stay in /app/fraud-dashboard
 * (Fraud Dashboard).
 */
export default function FraudHub(): JSX.Element {
  return (
    <div className="flex flex-col gap-8">
      {/* Period selector sits ABOVE the page header so the user always sees
          the active window before reading any content. defaultPeriod="D"
          because the Hub is the "what's on fire today" surface. */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Fraud Hub</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            What needs your attention right now.
          </p>
        </div>
        {/* LTD, not M — see the note in FraudDashboard.tsx. Windowing on
            alert_time (detector run time, not fraud time) left the
            investigator's desk listing 2 vehicles right after a
            39,572-vehicle watchlist. */}
        <TimePeriodSelector defaultPeriod="LTD" />
      </div>

      <HubKpiTiles />
      <SeverityTrendChart />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <TopRulesPanel />
        <GeoClusterMap />
      </div>

      {/* Critical Board (period-independent — "open" is a status) sits next
          to the Top Offenders panel (period-aware). 1fr_320px on lg keeps
          the Board wide enough for the dense table while pinning the
          offenders list to a narrow side rail. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
        <CriticalAlertsBoard />
        <TopOffendersPanel />
      </div>

      {/* Weekend/After-Hours + Station Risk tile groups (Plan 5 / Task 4) —
          story-first KPI panels backed by /dashboards/weekend-hours and
          /dashboards/station-risk. Sit after the drill-down tables since
          they're supplementary risk signals, not the primary triage view. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <WeekendAfterHoursTiles />
        <StationRiskTiles />
      </div>

      <DrillDeeperLinks />
    </div>
  );
}
